<script setup lang="ts">
/**
 * Web chat visitor card (customer tab), owner 27 ก.ย. 69 "ควรบอกละเอียดกว่านี้เผื่อเราช่วยอะไรลูกค้าได้" — trimmed
 * 17 ต.ค. 69 ("ข้อมูลเยอะเกินไป" → only what helps sell or solve a problem; "ตัดเลย ยกเว้น บัญชีบนเว็บ"):
 *   top      phone / e-mail they left (+ the e-mail notice: sent / waits / not sent) · the page they are on · their
 *            account on the site · on the site now or not
 *   1 ตะกร้า  only once there is a cart: pack · chosen/total meals · menus × qty · step + how long on it
 *   2 เว็บแจ้ง only when the site showed them something (payment failed, paused area, fee/date quoted, coupon …)
 *   3 คำสั่งซื้อ only for a customer the site vouched for — logged in on the order site (signed identity: `identified`)
 *            or the POS customer staff linked: POS orders (waiting for payment, latest, how often they bought). Never by
 *            a phone the browser sent (owner 17 ต.ค. 69 "เชื่อเฉพาะที่เว็บยืนยัน"): the number typed in the chat and the
 *            account the website context names show "ยังไม่ยืนยัน" unless the login is signed
 * Device, where they came from and the pages they opened are no longer shown here (still stored: marketing).
 * 1, 2 and the account line come from the customer's browser (display only — never prices or orders); 3 comes from POS.
 * "บล็อกผู้ส่งนี้" (WebBlockPanel): a banner at the top while blocked · blocking itself moved to the chat header's "…"
 * menu (owner 17 ต.ค. 69, lean panel — chat/ChatMoreMenu.vue).
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import {
  KIND_LABEL,
  MODE_LABEL,
  STEP_LABEL,
  baht,
  emailNoticeText,
  monthYear,
  payMethodLabel,
  posDate,
  presenceText,
  relTime,
  shortUrl,
  spanText,
} from '../../lib/webchat'
import { useClock } from '../../stores/clock'
import { useInbox } from '../../stores/inbox'
import { useOrders } from '../../stores/order'
import { useWebchat } from '../../stores/webchat'
import Icon from '../ui/Icon.vue'
import WebBlockPanel from './WebBlockPanel.vue'

const props = defineProps<{ convId: string }>()
const web = useWebchat()
const inbox = useInbox()
const clock = useClock()
const posOrders = useOrders()

const ITEMS_SHOWN = 5
const EVENTS_SHOWN = 3

const allItems = ref(false)
const allEvents = ref(false)

watch(
  () => props.convId,
  (id) => {
    allItems.value = allEvents.value = false
    web.watchCard(id)
  },
  { immediate: true },
)
onBeforeUnmount(() => web.stopCard())

const v = computed(() => web.visitors[props.convId] ?? null)
const ctx = computed(() => v.value?.context ?? null)
const presence = computed(() => (v.value ? presenceText(v.value.last_seen_at, clock.now) : null))
const test = computed(() => v.value?.site === 'demo' || v.value?.site === 'localhost')
const pageUrl = computed(() => v.value?.current_page?.url ?? null)
const visitorName = computed(() => v.value?.name || inbox.selected?.contact.name || null)
// แจ้งทางอีเมลเมื่อทีมงานตอบ (api/app/webchat/notices.py): the newest notice of this chat
const notice = computed(() => emailNoticeText(v.value?.email_notice ?? null, clock.now))

// ---- 1 cart
const stepText = computed(() => {
  const s = ctx.value?.step
  if (!s) return null
  const label = STEP_LABEL[s] ?? STEP_LABEL.other
  const span = spanText(ctx.value?.step_since ?? null, clock.now)
  return span ? `${label} · อยู่ขั้นนี้ ${span}` : label
})
const cart = computed(() => ctx.value?.cart ?? null)
const items = computed(() => cart.value?.items ?? [])
const shownItems = computed(() => (allItems.value ? items.value : items.value.slice(0, ITEMS_SHOWN)))
const progress = computed(() => {
  const c = cart.value
  if (!c || typeof c.chosen !== 'number' || !c.total) return null
  return { chosen: c.chosen, total: c.total, pct: Math.min(100, Math.round((c.chosen / c.total) * 100)) }
})
const hasCart = computed(() => !!(ctx.value?.pack?.name || items.value.length || progress.value))
const modeText = computed(() => (ctx.value?.mode ? (MODE_LABEL[ctx.value.mode] ?? null) : null))

// ---- 2 what the site showed
const events = computed(() => ctx.value?.events ?? [])
const shownEvents = computed(() => (allEvents.value ? events.value : events.value.slice(0, EVENTS_SHOWN)))
const kindLabel = (k: string): string => KIND_LABEL[k] ?? KIND_LABEL.notice!
const levelIcon = (l: string) => (l === 'error' ? 'alertCircle' : l === 'warn' ? 'alert' : 'info') as 'alertCircle' | 'alert' | 'info'

// ---- 3 POS orders
const orders = computed(() => web.orders[props.convId] ?? null)
const ordersBusy = computed(() => !!web.ordersLoading[props.convId])
// only for a customer the site vouched for (signed login) or staff linked — no "nothing to look up" line
const showOrders = computed(() => !!orders.value && orders.value.state !== 'no_customer')
// signed in on the site, or linked just now (order tab / "ผูกเบอร์"): ask again for the new customer set
const lookupKey = computed(() => (v.value ? `${v.value.identified}|${posOrders.contexts[props.convId]?.customer_id ?? ''}` : null))
watch(
  lookupKey,
  (key, old) => {
    if (key !== null && key !== old) void web.loadOrders(props.convId)
  },
  { immediate: true },
)
const whoLine = computed(() => {
  const w = orders.value?.who ?? []
  if (!w.length) return null
  return w.map((x) => (x === 'login' ? 'ลูกค้าที่ล็อกอินบนเว็บ (เว็บยืนยันแล้ว)' : 'ลูกค้าที่ผูกไว้กับแชทนี้')).join(' · ')
})
const customerLine = computed(() => {
  const cs = orders.value?.customers ?? []
  if (!cs.length) return null
  const since = monthYear(cs[0]?.since ?? null)
  return cs.map((c) => c.name).join(', ') + (cs.length === 1 && since ? ` · ลูกค้าตั้งแต่ ${since}` : '')
})
function deliveryText(date: string | null, time: string | null): string | null {
  const d = posDate(date, clock.now)
  return d ? `ส่ง ${d}${time && time !== '00:00' ? ` ${time} น.` : ''}` : null
}
function unpaidSub(u: NonNullable<NonNullable<typeof orders.value>['unpaid']>[number]): string {
  const exp = posDate(u.expires_at, clock.now, true)
  return [deliveryText(u.delivery_date, null), payMethodLabel(u.method), u.expired ? 'หมดเวลาชำระแล้ว' : exp ? `ชำระได้ถึง ${exp} น.` : null]
    .filter(Boolean)
    .join(' · ')
}
function refreshOrders(): void {
  void web.loadOrders(props.convId, true)
}

// ---- their account on the order site (kept by the owner 17 ต.ค. 69) — the browser's word; a login is verified only
// when the site signed it (`identified`), anything else that says "logged in" (and every typed phone) is "ยังไม่ยืนยัน"
const member = computed(() => ctx.value?.member ?? null)
const memberUnverified = computed(() => !!member.value?.logged_in && !v.value?.identified)
const memberText = computed(() => {
  const m = member.value
  if (!m) return null
  if (!m.logged_in) return 'ยังไม่ได้ล็อกอิน'
  return ['ล็อกอินแล้ว', m.name, m.phone].filter(Boolean).join(' · ')
})

async function copyEmail(): Promise<void> {
  const email = v.value?.email
  if (!email) return
  try {
    await navigator.clipboard.writeText(email)
    inbox.flash('คัดลอกอีเมลแล้ว')
  } catch {
    inbox.flash('คัดลอกไม่ได้ เลือกอีเมลแล้วคัดลอกเองได้')
  }
}

async function copyPhone(): Promise<void> {
  const phone = v.value?.phone
  if (!phone) return
  try {
    await navigator.clipboard.writeText(phone.replace(/-/g, ''))
    inbox.flash('คัดลอกเบอร์แล้ว')
  } catch {
    inbox.flash('คัดลอกไม่ได้ เลือกเบอร์แล้วคัดลอกเองได้')
  }
}
</script>

<template>
  <section v-if="v" class="cs wv" aria-label="แชทจากหน้าเว็บ">
    <div class="sec-h">
      <Icon name="globe" />
      <span class="sec-t">แชทจากหน้าเว็บ</span>
      <span v-if="test" class="wv-test">หน้าทดสอบ</span>
      <span class="grow"></span>
      <span v-if="presence" class="wv-pres" :class="{ on: presence.online }"><span class="wv-dot" aria-hidden="true"></span>{{ presence.text }}</span>
    </div>
    <WebBlockPanel :conv-id="convId" mode="banner" :name="visitorName" />
    <dl class="wv-list">
      <div v-if="v.phone" class="wv-row">
        <dt>เบอร์ที่ลูกค้ากรอก</dt>
        <dd class="wv-phone">
          <span class="mono">{{ v.phone }}</span>
          <span class="wv-unv" title="ลูกค้าพิมพ์เอง ไม่ได้ใช้ค้นคำสั่งซื้อ">ยังไม่ยืนยัน</span>
          <button class="wv-copy" type="button" @click="copyPhone">คัดลอก</button>
        </dd>
      </div>
      <div v-if="v.email" class="wv-row">
        <dt>อีเมลที่ลูกค้าฝากไว้</dt>
        <dd>
          <span class="wv-phone">
            <span class="mono wv-email">{{ v.email }}</span>
            <button class="wv-copy" type="button" @click="copyEmail">คัดลอก</button>
          </span>
          <span v-if="v.email_opt_out" class="wv-sub">ยกเลิกรับอีเมลแจ้งเตือนแล้ว</span>
        </dd>
      </div>
      <div v-if="notice" class="wv-row">
        <dt>แจ้งทางอีเมล</dt>
        <dd class="wv-notice" :class="`tone-${notice.tone}`">
          <Icon :name="notice.tone === 'ok' ? 'checkCircle' : notice.tone === 'wait' ? 'clock' : 'info'" /><span>{{ notice.text }}</span>
        </dd>
      </div>
      <div v-if="v.current_page && pageUrl" class="wv-row">
        <dt>หน้าที่เปิดอยู่</dt>
        <dd>
          <a class="wv-link" :href="pageUrl" target="_blank" rel="noopener noreferrer"
            ><span>{{ v.current_page.title || shortUrl(pageUrl) }}</span><Icon name="external"
          /></a>
        </dd>
      </div>
      <div v-if="memberText" class="wv-row">
        <dt>บัญชีบนเว็บ</dt>
        <dd :class="{ 'wv-acct': memberUnverified }">
          <span>{{ memberText }}</span>
          <span v-if="memberUnverified" class="wv-unv" title="เว็บสั่งซื้อไม่ได้ยืนยันว่าลูกค้าคนนี้ล็อกอินจริง ไม่ได้ใช้ค้นคำสั่งซื้อ">ยังไม่ยืนยัน</span>
        </dd>
      </div>
    </dl>
    <div v-if="v.identified" class="wv-id"><Icon name="checkCircle" /><span>ล็อกอินบนเว็บสั่งซื้อแล้ว ผูกกับลูกค้าใน POS ให้เอง</span></div>

    <!-- 1 · cart + step (only once there is a cart) -->
    <div v-if="hasCart" class="wv-sec wv-cart" role="group" aria-label="ตะกร้าบนเว็บ">
      <div class="wv-h">
        <Icon name="cart" />
        <span class="wv-ht">ตะกร้าบนเว็บ</span>
      </div>
      <span v-if="stepText" class="wv-step"><Icon name="clock" /><span>{{ stepText }}</span></span>
      <template v-if="ctx">
        <div v-if="ctx.pack?.name" class="wv-pack">
          <span>{{ ctx.pack.name }}</span>
          <span v-if="modeText" class="wv-tag">{{ modeText }}</span>
        </div>
        <div v-if="progress" class="wv-prog" :class="{ full: progress.chosen >= progress.total }">
          <span class="wv-prog-t">เลือกแล้ว <b class="mono">{{ progress.chosen }}/{{ progress.total }}</b> มื้อ</span>
          <span class="wv-bar" role="progressbar" :aria-valuenow="progress.chosen" :aria-valuemax="progress.total" aria-valuemin="0"
            ><i :style="{ width: `${progress.pct}%` }"></i
          ></span>
        </div>
        <ul v-if="items.length" class="wv-items">
          <li v-for="(it, i) in shownItems" :key="`${i}-${it.name}`">
            <span class="wv-item-n">{{ it.name }}</span><span class="wv-item-q mono">×{{ it.qty }}</span>
          </li>
        </ul>
        <button v-if="items.length > ITEMS_SHOWN" class="wv-more" type="button" @click="allItems = !allItems">
          {{ allItems ? 'ย่อรายการ' : `ดูอีก ${items.length - ITEMS_SHOWN} รายการ` }}
        </button>
      </template>
    </div>

    <!-- 2 · what the site just showed the customer (only when it showed something) -->
    <div v-if="events.length" class="wv-sec" role="group" aria-label="หน้าเว็บแจ้งลูกค้า">
      <div class="wv-h">
        <Icon name="bell" />
        <span class="wv-ht">หน้าเว็บแจ้งลูกค้า</span>
      </div>
      <ul class="wv-evs">
        <li v-for="(e, i) in shownEvents" :key="`${i}-${e.at}`" class="wv-ev" :class="`lv-${e.level}`">
          <Icon :name="levelIcon(e.level)" />
          <div class="wv-ev-b">
            <div class="wv-ev-h">
              <span class="wv-ev-k">{{ kindLabel(e.kind) }}</span>
              <span class="wv-ev-t">{{ relTime(e.at, clock.now) }}</span>
            </div>
            <p class="wv-ev-x">{{ e.text }}</p>
          </div>
        </li>
      </ul>
      <button v-if="events.length > EVENTS_SHOWN" class="wv-more" type="button" @click="allEvents = !allEvents">
        {{ allEvents ? 'ย่อรายการ' : `ดูทั้งหมด ${events.length} รายการ` }}
      </button>
    </div>

    <!-- 3 · POS orders of the customer the site vouched for (signed login) or staff linked -->
    <div v-if="showOrders && orders" class="wv-sec wv-ord" role="group" aria-label="คำสั่งซื้อของลูกค้านี้">
      <div class="wv-h">
        <Icon name="receipt" />
        <span class="wv-ht">คำสั่งซื้อของลูกค้านี้</span>
        <span class="grow"></span>
        <span class="wv-src">จาก POS</span>
        <button
          class="wv-ref"
          :class="{ busy: ordersBusy }"
          type="button"
          :disabled="ordersBusy"
          aria-label="โหลดคำสั่งซื้อใหม่"
          title="โหลดใหม่"
          @click="refreshOrders"
        >
          <Icon name="refresh" />
        </button>
      </div>
      <template v-if="orders">
        <template v-if="orders.state !== 'no_customer'">
          <p v-if="whoLine" class="wv-who">{{ whoLine }}</p>
          <p v-if="orders.state === 'not_found'" class="wv-empty">ไม่พบลูกค้านี้ใน POS</p>
          <p v-else-if="orders.state === 'pos_error'" class="wv-empty err">{{ orders.message || 'ต่อ POS ไม่ได้ ลองใหม่อีกครั้ง' }}</p>
          <template v-else>
            <p v-if="customerLine" class="wv-cus">{{ customerLine }}</p>
            <ul class="wv-orders">
              <li v-for="u in orders.unpaid ?? []" :key="`u-${u.ref}`" class="wv-o" :class="{ due: !u.expired }">
                <div class="wv-o-h">
                  <span class="wv-badge" :class="u.expired ? 'mut' : 'due'">{{ u.expired ? 'หมดเวลาชำระ' : 'รอชำระ' }}</span>
                  <span class="wv-o-ref mono">{{ u.ref }}</span>
                  <span class="grow"></span>
                  <span class="wv-o-amt mono">{{ baht(u.total) }}</span>
                </div>
                <p class="wv-o-sub">{{ unpaidSub(u) }}</p>
              </li>
              <li v-if="orders.latest" class="wv-o">
                <div class="wv-o-h">
                  <span class="wv-badge">ล่าสุด</span>
                  <span class="wv-o-ref mono">{{ orders.latest.ref }}</span>
                  <span class="grow"></span>
                  <span class="wv-o-amt mono">{{ baht(orders.latest.total) }}</span>
                </div>
                <p class="wv-o-sub">
                  {{ [deliveryText(orders.latest.delivery_date, null), orders.latest.cancelled ? 'ยกเลิก' : orders.latest.status_text].filter(Boolean).join(' · ') }}
                </p>
              </li>
            </ul>
            <p v-if="orders.unpaid === null" class="wv-note-s">POS รุ่นนี้ยังไม่บอกคำสั่งซื้อที่รอชำระ</p>
            <p v-if="orders.stats && !orders.stats.purchases" class="wv-empty">ยังไม่เคยซื้อ</p>
            <div v-else-if="orders.stats" class="stats wv-stats">
              <div class="st">
                <span class="st-l">เคยซื้อ</span>
                <span class="st-v">{{ orders.stats.purchases.toLocaleString('en-US') }} ครั้ง</span>
              </div>
              <div class="st">
                <span class="st-l">ยอดรวม</span>
                <span class="st-v mono">{{ baht(orders.stats.total) }}</span>
              </div>
              <div class="st">
                <span class="st-l">สั่งล่าสุด</span>
                <span class="st-v">{{ posDate(orders.stats.last_at, clock.now) ?? '—' }}</span>
              </div>
            </div>
            <p v-if="orders.stats?.partial" class="wv-note-s">นับจาก 50 คำสั่งซื้อล่าสุด</p>
          </template>
        </template>
      </template>
    </div>

    <p v-if="hasCart || events.length" class="wv-note">ตะกร้าและข้อความหน้าเว็บมาจากเบราว์เซอร์ของลูกค้า ใช้ดูประกอบเท่านั้น ราคาและคำสั่งซื้อจริงดูจาก POS</p>
  </section>
</template>

<style scoped>
.wv .sec-h :deep(.ic) {
  width: 16px;
  height: 16px;
  color: var(--mut);
}
.wv-test {
  height: 22px;
  padding: 0 8px;
  border-radius: 6px;
  background: var(--warns);
  color: #7a4700;
  font-size: 12px;
  font-weight: 600;
  line-height: 22px;
}
.wv-pres {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--mut);
  font-size: 12px;
  line-height: 18px;
}
.wv-pres.on {
  color: var(--ac);
  font-weight: 600;
}
.wv-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #b7c3bd;
}
.wv-pres.on .wv-dot {
  background: var(--ac);
}
.wv-list {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.wv-row {
  display: grid;
  grid-template-columns: 112px minmax(0, 1fr);
  gap: 8px;
  align-items: baseline;
}
.wv-row dt {
  color: var(--mut);
  font-size: 12px;
  line-height: 20px;
}
.wv-row dd {
  margin: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  font-size: 13px;
  line-height: 20px;
  overflow-wrap: anywhere;
}
.wv-phone {
  flex-direction: row !important;
  align-items: center;
  gap: 10px;
}
.wv-phone .mono {
  font-size: 14px;
  font-weight: 600;
}
.wv-email {
  font-size: 13px !important;
  font-weight: 600;
  overflow-wrap: anywhere;
  min-width: 0;
}
.wv-notice {
  flex-direction: row !important;
  align-items: flex-start;
  gap: 6px;
}
.wv-notice :deep(.ic) {
  width: 14px;
  height: 14px;
  flex: none;
  margin-top: 3px;
}
.wv-notice.tone-ok {
  color: var(--ac2);
}
.wv-notice.tone-wait {
  color: var(--warn);
}
.wv-notice.tone-off {
  color: var(--mut);
}
.wv-copy {
  flex: none;
  height: 26px;
  padding: 0 10px;
  border-radius: 6px;
  border: 1px solid var(--line);
  color: var(--ac);
  font-size: 12px;
  font-weight: 600;
  line-height: 24px;
}
.wv-copy:hover {
  background: var(--acs);
}
.wv-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--ac);
  font-weight: 600;
  text-decoration: none;
}
.wv-link:hover span {
  text-decoration: underline;
}
.wv-link :deep(.ic) {
  width: 13px;
  height: 13px;
  flex: none;
}
.wv-unv {
  flex: none;
  height: 20px;
  padding: 0 6px;
  border-radius: 6px;
  border: 1px solid var(--line);
  color: var(--mut);
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  white-space: nowrap;
}
.wv-acct {
  flex-direction: row !important;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
}
.wv-sub,
.wv-sub-only {
  color: var(--mut);
  font-size: 12px;
  line-height: 18px;
}
.wv-id {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  background: var(--acs);
  color: var(--ac2);
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
}
.wv-id :deep(.ic) {
  width: 15px;
  height: 15px;
  flex: none;
}

/* ---- sections */
.wv-sec {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid var(--line2);
}
.wv-h {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 24px;
}
.wv-h > :deep(.ic) {
  width: 15px;
  height: 15px;
  flex: none;
  color: var(--mut);
}
.wv-ht {
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
}
.wv-src {
  color: var(--mut);
  font-size: 12px;
  line-height: 18px;
  white-space: nowrap;
}
.wv-empty {
  margin: 0;
  color: var(--mut);
  font-size: 12px;
  line-height: 18px;
}
.wv-empty.err {
  color: var(--crit);
}
.wv-more {
  align-self: flex-start;
  height: 24px;
  padding: 0;
  color: var(--ac);
  font-size: 12px;
  font-weight: 600;
  line-height: 24px;
}
.wv-more:hover {
  text-decoration: underline;
}

/* 1 cart */
.wv-step {
  align-self: flex-start;
  display: inline-flex;
  align-items: flex-start;
  gap: 5px;
  max-width: 100%;
  padding: 2px 8px 2px 6px;
  border-radius: 6px;
  background: var(--acs);
  color: var(--ac2);
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
}
.wv-step :deep(.ic) {
  width: 13px;
  height: 13px;
  margin-top: 2.5px;
  flex: none;
}
.wv-pack {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 8px;
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
}
.wv-tag {
  height: 20px;
  padding: 0 6px;
  border-radius: 5px;
  border: 1px solid var(--line);
  color: var(--mut);
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
}
.wv-prog {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 10px;
}
.wv-prog-t {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
  white-space: nowrap;
}
.wv-prog-t b {
  color: var(--ink);
  font-size: 13px;
}
.wv-bar {
  height: 6px;
  border-radius: 3px;
  background: var(--line2);
  overflow: hidden;
}
.wv-bar i {
  display: block;
  height: 100%;
  border-radius: 3px;
  background: #8fc9b1;
}
.wv-prog.full .wv-bar i {
  background: var(--ac);
}
.wv-items {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
}
.wv-items li {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 3px 0;
  font-size: 13px;
  line-height: 20px;
}
.wv-items li + li {
  border-top: 1px dashed var(--line2);
}
.wv-item-n {
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
}
.wv-item-q {
  flex: none;
  color: var(--mut);
  font-size: 12px;
}

/* 2 what the site showed */
.wv-evs {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.wv-ev {
  display: grid;
  grid-template-columns: 16px minmax(0, 1fr);
  gap: 8px;
  align-items: start;
}
.wv-ev > :deep(.ic) {
  width: 16px;
  height: 16px;
  margin-top: 2px;
  color: var(--mut);
}
.wv-ev.lv-error > :deep(.ic) {
  color: var(--crit);
}
.wv-ev.lv-warn > :deep(.ic) {
  color: var(--warn);
}
.wv-ev-h {
  display: flex;
  align-items: baseline;
  gap: 8px;
}
.wv-ev-k {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  color: var(--mut);
}
.wv-ev.lv-error .wv-ev-k {
  color: var(--crit);
}
.wv-ev.lv-warn .wv-ev-k {
  color: var(--warn);
}
.wv-ev-t {
  flex: none;
  color: var(--mut);
  font-size: 12px;
  line-height: 18px;
  white-space: nowrap;
}
.wv-ev-x {
  margin: 0;
  font-size: 13px;
  line-height: 20px;
  overflow-wrap: anywhere;
}

/* 3 POS orders */
.wv-ref {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  margin-right: -4px;
  border-radius: 6px;
  color: var(--mut);
}
.wv-ref:hover:not(:disabled) {
  background: var(--line2);
  color: var(--ink);
}
.wv-ref :deep(.ic) {
  width: 15px;
  height: 15px;
}
.wv-ref.busy :deep(.ic) {
  animation: wv-spin 0.9s linear infinite;
}
@keyframes wv-spin {
  to {
    transform: rotate(360deg);
  }
}
.wv-who {
  margin: 0;
  color: var(--mut);
  font-size: 12px;
  line-height: 18px;
  overflow-wrap: anywhere;
}
.wv-cus {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  overflow-wrap: anywhere;
}
.wv-orders {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.wv-orders:empty {
  display: none;
}
.wv-o {
  padding: 8px 10px;
  border: 1px solid var(--line);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.wv-o.due {
  border-color: #efd9a8;
  background: #fffaf0;
}
.wv-o-h {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.wv-badge {
  flex: none;
  height: 20px;
  padding: 0 6px;
  border-radius: 5px;
  background: var(--line2);
  color: var(--mut);
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
}
.wv-badge.due {
  background: var(--warns);
  color: #7a4700;
}
.wv-o-ref {
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  overflow-wrap: anywhere;
}
.wv-o-amt {
  flex: none;
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
}
.wv-o-sub {
  margin: 0;
  color: var(--mut);
  font-size: 12px;
  line-height: 18px;
  overflow-wrap: anywhere;
}
.wv-stats .st {
  padding: 8px 10px;
}
.wv-stats .st-v {
  font-size: 14px;
}
.wv-note-s {
  margin: 0;
  color: var(--mut);
  font-size: 12px;
  line-height: 18px;
}

/* 4 visit */
.wv-ad {
  flex-direction: row !important;
  align-items: center;
  gap: 6px;
}
.wv-ad :deep(.ic) {
  width: 14px;
  height: 14px;
  flex: none;
  color: var(--mut);
}
.wv-trail {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
}
.wv-trail li {
  position: relative;
  display: grid;
  grid-template-columns: 14px minmax(0, 1fr) auto;
  gap: 8px;
  align-items: baseline;
  padding: 3px 0;
  font-size: 13px;
  line-height: 20px;
}
.wv-trail li:not(.wv-trail-more)::before {
  content: '';
  position: absolute;
  left: 4px;
  top: -3px;
  bottom: 50%;
  border-left: 1px solid var(--line);
}
.wv-trail li:first-child::before,
.wv-trail li.wv-trail-more + li::before {
  display: none;
}
.wv-trail-more {
  grid-template-columns: 1fr !important;
}
.wv-trail-dot {
  position: relative;
  z-index: 1;
  width: 9px;
  height: 9px;
  margin-top: 1px;
  border-radius: 50%;
  border: 2px solid #b7c3bd;
  background: var(--sf);
}
.wv-trail li.now .wv-trail-dot {
  border-color: var(--ac);
  background: var(--ac);
}
.wv-trail-n {
  min-width: 0;
  overflow-wrap: anywhere;
}
.wv-trail li.now .wv-trail-n {
  font-weight: 600;
}
.wv-trail-t {
  color: var(--mut);
  font-size: 12px;
  line-height: 18px;
  white-space: nowrap;
}
.wv-note {
  margin: 0;
  color: var(--mut);
  font-size: 12px;
  line-height: 18px;
}
</style>
